import { useEffect, useMemo, useRef, useState } from 'react';
import { useConfigService } from '@thd-nucleus/experience-context';

import {
  parseLaundryProducts,
  formatValuesIntoArray,
  getUpdatedLaundryBundle,
  getLaundrySelectedProducts
} from '../utils/product-utils';
import { useDataProducts } from './useDataProducts';
import { NONE_VALUE } from '../constants/index';

export const useLaundryEntryPoint = ({ bundle }) => {
  const isLaundryEntryPointEnabled = useConfigService('fs:isLaundryEntryPointEnabled') || false;
  const isVariantSwap = useRef(false);
  const updateProductRef = useRef(null);
  const [productsState, setProductsState] = useState(null);
  const [productIds, setProductIds] = useState(null);
  const configSelectableVariant = formatValuesIntoArray(useConfigService('selectableVariant') || null);
  const productsCacheRef = useRef({});

  // TODO: we need to update the accessory data, once the contract is defined for accessories
  const updatedBundle = getUpdatedLaundryBundle(bundle, configSelectableVariant);

  const bundleProductIds = updatedBundle?.bundleSpecificationDetails?.components?.map(
    ({ defaultProductId }) => defaultProductId
  );

  const itemIds = productIds || bundleProductIds;
  const skip = !isLaundryEntryPointEnabled || !itemIds || itemIds.length === 0;

  const { data, loading, error } = useDataProducts({
    itemIds,
    skip
  });

  const saveProductsInCache = (products) => {
    products.forEach((product) => {
      productsCacheRef.current[product.itemId] = product;
    });
  };

  const getProductFromCache = (productId) => {
    return productsCacheRef.current[productId];
  };

  const updateProducts = (newProduct, category) => {
    const currentProduct = productsState?.[category];
    const newProductsData = {
      ...productsState,
      [category]: {
        ...newProduct,
        selectableVariants: currentProduct?.selectableVariants,
        category
      }
    };
    setProductsState(newProductsData);
  };

  const unselectProduct = (category) => {
    const newProduct = {
      ...productsState?.[category],
      selectedVariant: NONE_VALUE
    };

    updateProducts(newProduct, category);
  };

  const swapVariant = ({ category, itemId }) => {
    isVariantSwap.current = true;

    if (itemId === NONE_VALUE) {
      unselectProduct(category);
      return;
    }
    const cachedProduct = getProductFromCache(itemId);
    if (cachedProduct) {
      updateProducts(cachedProduct, category);
      return;
    }

    updateProductRef.current = { category, itemId };
    setProductIds([itemId]);
  };

  useEffect(() => {
    if (!data?.products) {
      return;
    }
    try {
      const updatedProducts = data?.products?.map((product) => ({
        ...product,
        selectedVariant: product?.itemId,
        quantity: 1,
        features: {
          directDeliveryCartMethod: true
        }
      }));
      if (updateProductRef.current) {
        const { category } = updateProductRef.current;
        const newProduct = {
          ...updatedProducts?.[0],
          selectedVariant: updatedProducts?.[0]?.itemId
        };
        updateProducts(newProduct, category);
        updateProductRef.current = null;
      } else {
        setProductsState(parseLaundryProducts(updatedBundle, updatedProducts));
      }
      saveProductsInCache(updatedProducts);
    } catch (errorProducts) {
      setProductsState({});
    }

  }, [data]);

  const selectedProducts = useMemo(() => getLaundrySelectedProducts(productsState || []), [productsState]);

  useEffect(() => {
    if (selectedProducts && selectedProducts?.length > 0) {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('product-bundle.products-changed', selectedProducts);
    }
  }, [selectedProducts]);

  return {
    productsData: productsState,
    error,
    loading: !skip && (loading || productsState == null) && !error && !isVariantSwap?.current,
    selectedProducts,

    // methods
    swapVariant
  };
};